import * as deMessages from './messages/de_DE.json';
import * as enGBMessages from './messages/en_GB.json';
import * as enUSMessages from './messages/en_US.json';
import * as esMessages from './messages/es_ES.json';
import * as frCAMessages from './messages/fr_CA.json';
import * as frFRMessages from './messages/fr_FR.json';
import * as itMessages from './messages/it_IT.json';
import * as jaMessages from './messages/ja_JP.json';
import * as koMessages from './messages/ko_KR.json';
import * as ptMessages from './messages/pt_BR.json';
import * as svMessages from './messages/sv_SE.json';
import * as TabLoc from '../gen-src/Localize';
import * as thMessages from './messages/th_TH.json';
import * as zhsMessages from './messages/zh_CN.json';
import * as zhtMessages from './messages/zh_TW.json';
// import * as gaMessages from './messages/ga_IE.json';

// In order to support running in Phoenix we create a local structure that contains the localized version of the engine strings.
// If the engine locale has been manually set, then we use this local set of messages.  Otherwise we use @tableau/localize.

// tslint:disable-next-line:no-any
const localMessageMap: { readonly [language: string]: any } = {
  ['en_GB']: postProcessMessages(enGBMessages),
  ['en_US']: postProcessMessages(enUSMessages),
  ['de_DE']: postProcessMessages(deMessages),
  ['es_ES']: postProcessMessages(esMessages),
  ['fr_CA']: postProcessMessages(frCAMessages),
  ['fr_FR']: postProcessMessages(frFRMessages),
  // ['ga_IE']: postProcessMessages(gaMessages),
  ['it_IT']: postProcessMessages(itMessages),
  ['ja_JP']: postProcessMessages(jaMessages),
  ['ko_KR']: postProcessMessages(koMessages),
  ['pt_BR']: postProcessMessages(ptMessages),
  ['sv_SE']: postProcessMessages(svMessages),
  ['th_TH']: postProcessMessages(thMessages),
  ['zh_CN']: postProcessMessages(zhsMessages),
  ['zh_TW']: postProcessMessages(zhtMessages),
};

const fallbackLocale = 'en_US';
// tslint:disable-next-line:no-any
let messageMap: any;

/**
 * Inject the metrics engine with the locale-specific information it needs.
 * @param messageLocale the Tableau-supported locale for message strings.
 * @param getLocalizeFormatOverride an optional platform specific override for `getLocalizeFormat` to allow use in react native.
 */
export function setEngineLocale(
  messageLocale: string,
  // tslint:disable-next-line: no-any
  getLocalizeFormatOverride?: () => any
): void {
  messageMap = localMessageMap[messageLocale] || localMessageMap[fallbackLocale];
  if (getLocalizeFormatOverride) {
    // tslint:disable-next-line:no-any whitespace
    (<any>TabLoc).getLocalizeFormat = getLocalizeFormatOverride;
  }
}

const getMessage = (messageId: string) =>
  messageMap[messageId] || localMessageMap[fallbackLocale][messageId] || 'LOCERR_' + messageId;

// tslint:disable-next-line:no-any
function postProcessMessages(messages: any): any {
  if (typeof messages.records !== 'undefined') {
    Object.keys(messages.records).forEach((key) => {
      messages[messages.records[key].id] = messages.records[key].message;
    });
    messages.records = null;
  } else {
    Object.keys(messages).forEach((key) => {
      const message = messages[key];
      if (typeof message !== 'string') {
        messages[key] = message.message;
      }
    });
  }

  return messages;
}

/**
 * Adding a string is a two-step process.
 * 1) The string itself needs to be defined in ./messages/en_US.json.
 * 2) an accessor method needs to be added to the Messages object below.
 */
export const Messages = {
  // tslint:disable-next-line:readonly-array
  formatString(str: string, args: { [id: string]: string }): string {
    return str.replace(/{(\w+)}/g, (match, index) => args[index] || '');
  },

  metricsComparisonNoChange(): string {
    return messageMap ? getMessage('metricsComparisonNoChange') : TabLoc.Messages.metricsComparisonNoChange();
  },
  metricsComparisonAtTarget(): string {
    return messageMap ? getMessage('metricsComparisonAtTarget') : TabLoc.Messages.metricsComparisonAtTarget();
  },
  metricsComparisonBelowTarget(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsComparisonBelowTarget'), a)
      : TabLoc.Messages.metricsComparisonBelowTarget(a);
  },
  metricsComparisonAboveTarget(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsComparisonAboveTarget'), a)
      : TabLoc.Messages.metricsComparisonAboveTarget(a);
  },
  metricsComparisonBelowTargetDetail(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsComparisonBelowTargetDetail'), a)
      : TabLoc.Messages.metricsComparisonBelowTargetDetail(a);
  },
  metricsComparisonAboveTargetDetail(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsComparisonAboveTargetDetail'), a)
      : TabLoc.Messages.metricsComparisonAboveTargetDetail(a);
  },
  metricsSecondComparisonSingular(): string {
    return messageMap
      ? getMessage('metricsSecondComparisonSingular')
      : TabLoc.Messages.metricsSecondComparisonSingular();
  },
  metricsSecondComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsSecondComparisonPlural'), a)
      : TabLoc.Messages.metricsSecondComparisonPlural(a);
  },
  metricsMinuteComparisonSingular(): string {
    return messageMap
      ? getMessage('metricsMinuteComparisonSingular')
      : TabLoc.Messages.metricsMinuteComparisonSingular();
  },
  metricsMinuteComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsMinuteComparisonPlural'), a)
      : TabLoc.Messages.metricsMinuteComparisonPlural(a);
  },
  metricsHourComparisonSingular(): string {
    return messageMap ? getMessage('metricsHourComparisonSingular') : TabLoc.Messages.metricsHourComparisonSingular();
  },
  metricsHourComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsHourComparisonPlural'), a)
      : TabLoc.Messages.metricsHourComparisonPlural(a);
  },
  metricsDayComparisonSingular(): string {
    return messageMap ? getMessage('metricsDayComparisonSingular') : TabLoc.Messages.metricsDayComparisonSingular();
  },
  metricsDayComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDayComparisonPlural'), a)
      : TabLoc.Messages.metricsDayComparisonPlural(a);
  },
  metricsWeekComparisonSingular(): string {
    return messageMap ? getMessage('metricsWeekComparisonSingular') : TabLoc.Messages.metricsWeekComparisonSingular();
  },
  metricsWeekComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsWeekComparisonPlural'), a)
      : TabLoc.Messages.metricsWeekComparisonPlural(a);
  },
  metricsMonthComparisonSingular(): string {
    return messageMap ? getMessage('metricsMonthComparisonSingular') : TabLoc.Messages.metricsMonthComparisonSingular();
  },
  metricsMonthComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsMonthComparisonPlural'), a)
      : TabLoc.Messages.metricsMonthComparisonPlural(a);
  },
  metricsQuarterComparisonSingular(): string {
    return messageMap
      ? getMessage('metricsQuarterComparisonSingular')
      : TabLoc.Messages.metricsQuarterComparisonSingular();
  },
  metricsQuarterComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsQuarterComparisonPlural'), a)
      : TabLoc.Messages.metricsQuarterComparisonPlural(a);
  },
  metricsYearComparisonSingular(): string {
    return messageMap ? getMessage('metricsYearComparisonSingular') : TabLoc.Messages.metricsYearComparisonSingular();
  },
  metricsYearComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsYearComparisonPlural'), a)
      : TabLoc.Messages.metricsYearComparisonPlural(a);
  },
  metricsMarkComparisonSingular(): string {
    return messageMap ? getMessage('metricsMarkComparisonSingular') : TabLoc.Messages.metricsMarkComparisonSingular();
  },
  metricsMarkComparisonPlural(a: { value: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsMarkComparisonPlural'), a)
      : TabLoc.Messages.metricsMarkComparisonPlural(a);
  },
  metricsNoValue(): string {
    return messageMap ? getMessage('metricsNoValue') : TabLoc.Messages.metricsNoValue();
  },
  metricsComparisonTimestamp(a: { first: string; last: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsComparisonTimestamp'), a)
      : TabLoc.Messages.metricsComparisonTimestamp(a);
  },
  metricsComparisonTimestampTwoLines(a: { first: string; last: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsComparisonTimestampTwoLines'), a)
      : TabLoc.Messages.metricsComparisonTimestampTwoLines(a);
  },
  metricsLegend(a: { start: string; end: string }): string {
    return messageMap ? Messages.formatString(getMessage('metricsLegend'), a) : TabLoc.Messages.metricsLegend(a);
  },
  metricsSyncedAtTime(a: { day: string; time: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsSyncedAtTime'), a)
      : TabLoc.Messages.metricsSyncedAtTime(a);
  },
  metricsLegendTwoLines(a: { start: string; end: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsLegendTwoLines'), a)
      : TabLoc.Messages.metricsLegendTwoLines(a);
  },
  metricsWeekOf(a: { week: string }): string {
    return messageMap ? Messages.formatString(getMessage('metricsWeekOf'), a) : TabLoc.Messages.metricsWeekOf(a);
  },
  metricsWeekOfTwoLines(a: { week: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsWeekOfTwoLines'), a)
      : TabLoc.Messages.metricsWeekOfTwoLines(a);
  },
  metricsFY(a: { year: string }): string {
    return messageMap ? Messages.formatString(getMessage('metricsFY'), a) : TabLoc.Messages.metricsFY(a);
  },
  metricsFYQuarter(a: { fy: string; qN: string }): string {
    return messageMap ? Messages.formatString(getMessage('metricsFYQuarter'), a) : TabLoc.Messages.metricsFYQuarter(a);
  },
  metricsNoComparisonRowSubtitle(): string {
    return messageMap ? getMessage('metricsNoComparisonRowSubtitle') : TabLoc.Messages.metricsNoComparisonRowSubtitle();
  },
  metricsTargetRowSubtitle(a: { targetValue: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsTargetRowSubtitle'), a)
      : TabLoc.Messages.metricsTargetRowSubtitle(a);
  },
  metricsCustomStartDateOption(): string {
    return messageMap ? getMessage('metricsCustomStartDateOption') : TabLoc.Messages.metricsCustomStartDateOption();
  },
  metricsDateRangeAllTime(): string {
    return messageMap ? getMessage('metricsDateRangeAllTime') : TabLoc.Messages.metricsDateRangeAllTime();
  },
  metricsDateRangeSecondPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeSecondPlural'), a)
      : TabLoc.Messages.metricsDateRangeSecondPlural(a);
  },
  metricsDateRangeSecondSingular(): string {
    return messageMap ? getMessage('metricsDateRangeSecondSingular') : TabLoc.Messages.metricsDateRangeSecondSingular();
  },
  metricsDateRangeMinutePlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeMinutePlural'), a)
      : TabLoc.Messages.metricsDateRangeMinutePlural(a);
  },
  metricsDateRangeMinuteSingular(): string {
    return messageMap ? getMessage('metricsDateRangeMinuteSingular') : TabLoc.Messages.metricsDateRangeMinuteSingular();
  },
  metricsDateRangeHourPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeHourPlural'), a)
      : TabLoc.Messages.metricsDateRangeHourPlural(a);
  },
  metricsDateRangeHourSingular(): string {
    return messageMap ? getMessage('metricsDateRangeHourSingular') : TabLoc.Messages.metricsDateRangeHourSingular();
  },
  metricsDateRangeDayPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeDayPlural'), a)
      : TabLoc.Messages.metricsDateRangeDayPlural(a);
  },
  metricsDateRangeDaySingular(): string {
    return messageMap ? getMessage('metricsDateRangeDaySingular') : TabLoc.Messages.metricsDateRangeDaySingular();
  },
  metricsDateRangeMonthPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeMonthPlural'), a)
      : TabLoc.Messages.metricsDateRangeMonthPlural(a);
  },
  metricsDateRangeMonthSingular(): string {
    return messageMap ? getMessage('metricsDateRangeMonthSingular') : TabLoc.Messages.metricsDateRangeMonthSingular();
  },
  metricsDateRangeWeekPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeWeekPlural'), a)
      : TabLoc.Messages.metricsDateRangeWeekPlural(a);
  },
  metricsDateRangeWeekSingular(): string {
    return messageMap ? getMessage('metricsDateRangeWeekSingular') : TabLoc.Messages.metricsDateRangeWeekSingular();
  },
  metricsDateRangeQuarterPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeQuarterPlural'), a)
      : TabLoc.Messages.metricsDateRangeQuarterPlural(a);
  },
  metricsDateRangeQuarterSingular(): string {
    return messageMap
      ? getMessage('metricsDateRangeQuarterSingular')
      : TabLoc.Messages.metricsDateRangeQuarterSingular();
  },
  metricsDateRangeYearPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeYearPlural'), a)
      : TabLoc.Messages.metricsDateRangeYearPlural(a);
  },
  metricsDateRangeYearSingular(): string {
    return messageMap ? getMessage('metricsDateRangeYearSingular') : TabLoc.Messages.metricsDateRangeYearSingular();
  },
  metricsDateRangeByMarkPlural(a: { num: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeByMarkPlural'), a)
      : TabLoc.Messages.metricsDateRangeByMarkPlural(a);
  },
  metricsDateRangeByMarkSingular(): string {
    return messageMap ? getMessage('metricsDateRangeByMarkSingular') : TabLoc.Messages.metricsDateRangeByMarkSingular();
  },
  metricsGoodStatusDescription(): string {
    return messageMap ? getMessage('metricsGoodStatusDescription') : TabLoc.Messages.metricsGoodStatusDescription();
  },
  metricsBadStatusDescription(): string {
    return messageMap ? getMessage('metricsBadStatusDescription') : TabLoc.Messages.metricsBadStatusDescription();
  },
  metricsNeutralStatusDescription(): string {
    return messageMap
      ? getMessage('metricsNeutralStatusDescription')
      : TabLoc.Messages.metricsNeutralStatusDescription();
  },
  metricsDateRangeSlashFormat(a: { startDate: string; endDate: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('metricsDateRangeSlashFormat'), a)
      : TabLoc.Messages.metricsDateRangeSlashFormat(a);
  },
  configurationInfoCustomStartDate(a: { date: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('configurationInfoCustomStartDate'), a)
      : TabLoc.Messages.configurationInfoCustomStartDate(a);
  },
  secondsWindowSingular(): string {
    return messageMap ? getMessage('secondsWindowSingular') : TabLoc.Messages.secondsWindowSingular();
  },
  secondsWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('secondsWindowPlural'), a)
      : TabLoc.Messages.secondsWindowPlural(a);
  },
  minutesWindowSingular(): string {
    return messageMap ? getMessage('minutesWindowSingular') : TabLoc.Messages.minutesWindowSingular();
  },
  minutesWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('minutesWindowPlural'), a)
      : TabLoc.Messages.minutesWindowPlural(a);
  },
  hoursWindowSingular(): string {
    return messageMap ? getMessage('hoursWindowSingular') : TabLoc.Messages.hoursWindowSingular();
  },
  hoursWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('hoursWindowPlural'), a)
      : TabLoc.Messages.hoursWindowPlural(a);
  },
  daysWindowSingular(): string {
    return messageMap ? getMessage('daysWindowSingular') : TabLoc.Messages.daysWindowSingular();
  },
  daysWindowPlural(a: { number: string }): string {
    return messageMap ? Messages.formatString(getMessage('daysWindowPlural'), a) : TabLoc.Messages.daysWindowPlural(a);
  },
  weeksWindowSingular(): string {
    return messageMap ? getMessage('weeksWindowSingular') : TabLoc.Messages.weeksWindowSingular();
  },
  weeksWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('weeksWindowPlural'), a)
      : TabLoc.Messages.weeksWindowPlural(a);
  },
  monthsWindowSingular(): string {
    return messageMap ? getMessage('monthsWindowSingular') : TabLoc.Messages.monthsWindowSingular();
  },
  monthsWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('monthsWindowPlural'), a)
      : TabLoc.Messages.monthsWindowPlural(a);
  },
  quartersWindowSingular(): string {
    return messageMap ? getMessage('quartersWindowSingular') : TabLoc.Messages.quartersWindowSingular();
  },
  quartersWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('quartersWindowPlural'), a)
      : TabLoc.Messages.quartersWindowPlural(a);
  },
  yearsWindowSingular(): string {
    return messageMap ? getMessage('yearsWindowSingular') : TabLoc.Messages.yearsWindowSingular();
  },
  yearsWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('yearsWindowPlural'), a)
      : TabLoc.Messages.yearsWindowPlural(a);
  },
  marksWindowSingular(): string {
    return messageMap ? getMessage('marksWindowSingular') : TabLoc.Messages.marksWindowSingular();
  },
  marksWindowPlural(a: { number: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('marksWindowPlural'), a)
      : TabLoc.Messages.marksWindowPlural(a);
  },
  windowPickerDefinedStart(a: { date: string }): string {
    return messageMap
      ? Messages.formatString(getMessage('windowPickerDefinedStart'), a)
      : TabLoc.Messages.windowPickerDefinedStart(a);
  },
  noTimeDimensionPlaceholder(): string {
    return messageMap ? getMessage('noTimeDimensionPlaceholder') : TabLoc.Messages.noTimeDimensionPlaceholder();
  },
  snapshotTimeDimension(): string {
    return messageMap ? getMessage('snapshotTimeDimension') : TabLoc.Messages.snapshotTimeDimension();
  },
};
